import {cn} from 'platform/shared';
import {match} from 'ts-pattern';

import {AriaRole, CSSProperties, ReactNode} from 'react';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {
  FLEX_ALIGN_CLASSES,
  FLEX_DIRECTION_CLASSES,
  FLEX_JUSTIFY_CLASSES,
} from '../../class-maps/flexClasses';
import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {FlexboxAlign} from '../../types/FlexboxAlign';
import {FlexboxDirection} from '../../types/FlexboxDirection';
import {FlexboxJustify} from '../../types/FlexboxJustify';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {getCssSize} from '../../utils/getCssSize';
import {getSize} from '../../utils/getSize';

function getJustifyClass(justify: FlexboxJustify) {
  return match(justify)
    .with('baseline', () => 'justify-baseline')
    .with('stretch', () => 'justify-stretch')
    .otherwise((value) => FLEX_JUSTIFY_CLASSES[value]);
}

export interface StackProps extends TestIdProps {
  children: ReactNode;
  direction: FlexboxDirection | ValueByDevice<FlexboxDirection>;
  spacing?: Integer | ValueByDevice<Integer | undefined>;
  align?: FlexboxAlign | ValueByDevice<FlexboxAlign | undefined>;
  justify?: FlexboxJustify | ValueByDevice<FlexboxJustify | undefined>;
  shrink?: number;
  grow?: number;
  basis?: CSSProperties['flexBasis'];
  width?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  height?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  maxWidth?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  maxHeight?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  minWidth?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  minHeight?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  wrap?: boolean | ValueByDevice<boolean | undefined> | 'nowrap';
  role?: AriaRole;
}

export function Stack(props: StackProps) {
  const align = useResponsivePropValue(props.align);
  const spacing = useResponsivePropValue(props.spacing);
  const direction = useResponsivePropValue(props.direction);
  const shouldWrap = useResponsivePropValue(props.wrap);
  const justify = useResponsivePropValue(props.justify);
  const width = useResponsivePropValue(props.width);
  const height = useResponsivePropValue(props.height);
  const maxWidth = useResponsivePropValue(props.maxWidth);
  const maxHeight = useResponsivePropValue(props.maxHeight);
  const minWidth = useResponsivePropValue(props.minWidth);
  const minHeight = useResponsivePropValue(props.minHeight);

  const wrapClass = match(shouldWrap)
    .with('nowrap', () => 'flex-nowrap')
    .with(true, () => 'flex-wrap')
    .otherwise(() => undefined);

  return (
    <div
      data-testid={suffixTestId('stack', props)}
      role={props.role}
      className={cn(
        'flex',
        FLEX_DIRECTION_CLASSES[direction ?? 'row'],
        isNil(align) ? undefined : FLEX_ALIGN_CLASSES[align],
        isNil(justify) ? undefined : getJustifyClass(justify),
        wrapClass
      )}
      style={{
        width: getCssSize(width),
        height: getCssSize(height),
        maxWidth: getCssSize(maxWidth),
        maxHeight: getCssSize(maxHeight),
        minWidth: getCssSize(minWidth),
        minHeight: getCssSize(minHeight),
        flexGrow: props.grow ?? 'initial',
        flexShrink: props.shrink ?? 'initial',
        flexBasis: props.basis ?? 'initial',
        overflow: shouldWrap === 'nowrap' ? 'hidden' : 'initial',
        gap: getSize(spacing ?? 0),
      }}
    >
      {props.children}
    </div>
  );
}
